import RAPIER from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import { BALL, COURT, GOAL } from '../game/constants';

export type ContactTag = 'board' | 'post' | 'crossbar' | 'net';

const FIXED_DT = 1 / 60;

export class PhysicsWorld {
  private world: RAPIER.World | null = null;
  private eventQueue: RAPIER.EventQueue | null = null;
  private accumulator = 0;
  private readonly wallTags = new Map<number, ContactTag>();
  private ballColliderHandle = -1;
  ballBody: RAPIER.RigidBody | null = null;

  onBallContact: ((tag: ContactTag, speed: number) => void) | null = null;

  async init(): Promise<void> {
    await RAPIER.init();
    this.world = new RAPIER.World({ x: 0, y: -21, z: 0 });
    this.world.timestep = FIXED_DT;
    this.eventQueue = new RAPIER.EventQueue(true);
    this.buildStaticColliders();
  }

  private buildStaticColliders(): void {
    const world = this.world;
    if (!world) return;
    const { halfLength, halfWidth, boardHeight, wallThickness } = COURT;
    const fixed = RAPIER.RigidBodyDesc.fixed();

    const floor = world.createRigidBody(fixed.setTranslation(0, -0.5, 0));
    world.createCollider(RAPIER.ColliderDesc.cuboid(60, 0.5, 60).setFriction(0.85).setRestitution(0.5), floor);

    const addWall = (x: number, y: number, z: number, hx: number, hy: number, hz: number, tag: ContactTag, restitution: number, friction = 0.4) => {
      const body = world.createRigidBody(fixed.setTranslation(x, y, z));
      const collider = world.createCollider(
        RAPIER.ColliderDesc.cuboid(hx, hy, hz)
          .setFriction(friction)
          .setRestitution(restitution)
          .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS),
        body,
      );
      this.wallTags.set(collider.handle, tag);
      return collider;
    };

    addWall(0, boardHeight / 2, -halfWidth - wallThickness / 2, halfLength + wallThickness, boardHeight / 2, wallThickness / 2, 'board', 0.62);
    addWall(0, boardHeight / 2, halfWidth + wallThickness / 2, halfLength + wallThickness, boardHeight / 2, wallThickness / 2, 'board', 0.62);

    const seg = halfWidth - GOAL.halfWidth;
    for (const side of [-1, 1] as const) {
      for (const s of [-1, 1] as const) {
        addWall(
          side * (halfLength + wallThickness / 2),
          boardHeight / 2,
          s * (GOAL.halfWidth + seg / 2),
          wallThickness / 2,
          boardHeight / 2,
          seg / 2,
          'board',
          0.62,
        );
      }
    }

    const rotZ90 = new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.PI / 2, 0, 0));
    for (const side of [-1, 1] as const) {
      for (const s of [-1, 1] as const) {
        const post = world.createRigidBody(fixed.setTranslation(side * halfLength, GOAL.height / 2, s * GOAL.halfWidth));
        const collider = world.createCollider(
          RAPIER.ColliderDesc.cylinder(GOAL.height / 2, GOAL.postRadius)
            .setFriction(0.2)
            .setRestitution(0.78)
            .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS),
          post,
        );
        this.wallTags.set(collider.handle, 'post');
      }
      const crossBody = world.createRigidBody(
        fixed.setTranslation(side * halfLength, GOAL.height, 0).setRotation({ x: rotZ90.x, y: rotZ90.y, z: rotZ90.z, w: rotZ90.w }),
      );
      const cross = world.createCollider(
        RAPIER.ColliderDesc.cylinder(GOAL.halfWidth, GOAL.postRadius)
          .setFriction(0.2)
          .setRestitution(0.78)
          .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS),
        crossBody,
      );
      this.wallTags.set(cross.handle, 'crossbar');

      addWall(side * (halfLength + GOAL.depth), GOAL.height / 2, 0, 0.04, GOAL.height / 2, GOAL.halfWidth, 'net', 0.02, 0.92);
      for (const s of [-1, 1] as const) {
        addWall(side * (halfLength + GOAL.depth / 2), GOAL.height / 2, s * GOAL.halfWidth, GOAL.depth / 2, GOAL.height / 2, 0.04, 'net', 0.02, 0.92);
      }
      addWall(side * (halfLength + GOAL.depth / 2), GOAL.height + 0.03, 0, GOAL.depth / 2, 0.04, GOAL.halfWidth, 'net', 0.02, 0.92);
    }
  }

  spawnBall(position: THREE.Vector3): void {
    const world = this.world;
    if (!world) return;
    const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(position.x, position.y, position.z)
      .setLinearDamping(0.42)
      .setAngularDamping(0.55)
      .setCcdEnabled(true);
    this.ballBody = world.createRigidBody(bodyDesc);
    const collider = world.createCollider(
      RAPIER.ColliderDesc.ball(BALL.radius)
        .setDensity(11)
        .setFriction(0.55)
        .setRestitution(0.58)
        .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS),
      this.ballBody,
    );
    this.ballColliderHandle = collider.handle;
  }

  resetBall(position: THREE.Vector3): void {
    const body = this.ballBody;
    if (!body) return;
    body.setTranslation({ x: position.x, y: position.y, z: position.z }, true);
    body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    body.setAngvel({ x: 0, y: 0, z: 0 }, true);
  }

  createBomb(position: THREE.Vector3, velocity: THREE.Vector3): RAPIER.RigidBody | null {
    const world = this.world;
    if (!world) return null;
    const body = world.createRigidBody(
      RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(position.x, position.y, position.z)
        .setLinvel(velocity.x, velocity.y, velocity.z)
        .setLinearDamping(0.12)
        .setAngularDamping(0.8)
        .setCcdEnabled(true),
    );
    world.createCollider(RAPIER.ColliderDesc.ball(0.17).setDensity(9).setFriction(0.5).setRestitution(0.35), body);
    return body;
  }

  createBoxCollider(position: THREE.Vector3): number {
    const world = this.world;
    if (!world) return -1;
    const body = world.createRigidBody(RAPIER.RigidBodyDesc.fixed().setTranslation(position.x, position.y, position.z));
    const collider = world.createCollider(
      RAPIER.ColliderDesc.cuboid(0.3, 0.3, 0.3).setFriction(0.5).setRestitution(0.5),
      body,
    );
    return collider.handle;
  }

  removeColliderByHandle(handle: number): void {
    const world = this.world;
    if (!world) return;
    const collider = world.getCollider(handle);
    if (!collider) return;
    const body = collider.parent();
    world.removeCollider(collider, true);
    if (body && body.numColliders() === 0) world.removeRigidBody(body);
  }

  removeBody(body: RAPIER.RigidBody | null): void {
    if (!this.world || !body) return;
    this.world.removeRigidBody(body);
  }

  step(delta: number): void {
    const world = this.world;
    const queue = this.eventQueue;
    if (!world || !queue) return;
    this.accumulator += Math.min(delta, 0.1);
    let steps = 0;
    while (this.accumulator >= FIXED_DT && steps < 5) {
      const incoming = this.ballBody?.linvel();
      const impactSpeed = incoming ? Math.hypot(incoming.x, incoming.y, incoming.z) : 0;
      world.step(queue);
      this.accumulator -= FIXED_DT;
      steps += 1;
      queue.drainCollisionEvents((h1, h2, started) => {
        if (!started || !this.onBallContact) return;
        const other = h1 === this.ballColliderHandle ? h2 : h2 === this.ballColliderHandle ? h1 : -1;
        if (other < 0) return;
        const tag = this.wallTags.get(other);
        if (!tag) return;
        // Nets absorb speed during the solver; feedback needs incoming energy.
        this.onBallContact(tag, impactSpeed);
      });
      this.containBallInGoalNet();
    }
    if (steps >= 5) this.accumulator = 0;
  }

  private containBallInGoalNet(): void {
    const body = this.ballBody;
    if (!body) return;
    const position = body.translation();
    const velocity = body.linvel();
    const absX = Math.abs(position.x);
    const goalDepth = absX - COURT.halfLength;
    if (goalDepth < -BALL.radius) return;

    const sideLimit = GOAL.halfWidth + 0.04 + BALL.radius;
    const roofLimit = GOAL.height + 0.04 + BALL.radius;
    const backLimit = COURT.halfLength + GOAL.depth - 0.04 - BALL.radius;
    let { x, y, z } = position;
    let { x: vx, y: vy, z: vz } = velocity;
    let corrected = false;

    if (y >= -BALL.radius && y <= roofLimit && Math.abs(z) <= sideLimit && absX > backLimit) {
      x = Math.sign(position.x) * backLimit;
      vx = velocity.x * Math.sign(position.x) > 0 ? -velocity.x * 0.02 : velocity.x * 0.8;
      corrected = true;
    }
    if (goalDepth <= GOAL.depth + BALL.radius && y >= -BALL.radius && y <= roofLimit && Math.abs(z) > sideLimit) {
      z = Math.sign(position.z) * sideLimit;
      vz = velocity.z * Math.sign(position.z) > 0 ? -velocity.z * 0.02 : velocity.z * 0.8;
      corrected = true;
    }
    if (goalDepth <= GOAL.depth + BALL.radius && Math.abs(z) <= sideLimit && y > roofLimit) {
      y = roofLimit;
      vy = velocity.y > 0 ? -velocity.y * 0.02 : velocity.y * 0.8;
      corrected = true;
    }
    if (corrected) {
      body.setTranslation({ x, y, z }, true);
      body.setLinvel({ x: vx, y: vy, z: vz }, true);
    }
  }

  get bodyCount(): number {
    return this.world?.bodies.len() ?? 0;
  }

  get colliderCount(): number {
    return this.world?.colliders.len() ?? 0;
  }

  dispose(): void {
    this.world?.free();
    this.world = null;
    this.eventQueue = null;
    this.ballBody = null;
    this.wallTags.clear();
  }
}
